/**
 * Localisation.
 *
 * Small enough to read in one sitting, which is why there is no i18n library
 * here. Two languages, static dictionaries, no pluralisation rules and no
 * runtime string interpolation — a library would add a dependency and an API
 * to learn without doing anything this file does not.
 *
 * ── How a locale is chosen ─────────────────────────────────────────────────
 * The URL decides what is rendered, and nothing else:
 *     /            /privacy          → English
 *     /es          /es/privacy       → Spanish
 *
 * The URL is the whole state of the page. That means a Spanish page can be
 * linked, shared, bookmarked and indexed by Google as its own page — none of
 * which is true when the language lives in storage. It is also what makes the
 * hreflang tags in <Seo> honest.
 *
 * A visitor's own language decides only which URL they *arrive* at: the server
 * may send a first-time Spanish-speaking visitor from / to /es before React
 * runs at all. Once a page is being rendered, that is over and done with, and
 * the path is again the only thing anyone reads. See
 * src/server/middleware/language.ts.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { type Locale } from '../../shared/waitlist';
import { pathInLocale } from '../../shared/language';

import en from './en';
import es from './es';
import type { Dictionary } from './types';

/*
 * Re-exported so the rest of the client keeps importing its locale helpers from
 * '../i18n' as it always has, while there is only one implementation and the
 * server shares it.
 */
export { localeFromPath, stripLocale, pathInLocale } from '../../shared/language';

const DICTIONARIES: Record<Locale, Dictionary> = { en, es };

interface I18nValue {
  locale: Locale;
  t: Dictionary;
  /**
   * Prefix an app path with the current locale.
   *   localePath('/privacy') → '/privacy'      when locale is 'en'
   *   localePath('/privacy') → '/es/privacy'   when locale is 'es'
   */
  localePath: (path: string) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const value = useMemo<I18nValue>(
    () => ({
      locale,
      t: DICTIONARIES[locale],
      localePath: (path: string) => pathInLocale(path, locale),
    }),
    [locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Access the current language, its strings and the path helper. */
export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error('useI18n must be used inside <I18nProvider>. Check main.tsx.');
  }
  return value;
}
